import { useMutation, useQuery } from '@tanstack/react-query';

import cycleInstanceService from './cycleInstanceService';

import type { EvaluationTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import type { CycleInstance } from '@/types/cycle-evaluation/CycleInstance';

import { queryClient } from '@/AppWithProviders';
import { ReviewQueryKey } from '@/constants/QueryKeys';

interface UseGetCycleInstanceOptions {
    includeInactiveQuestions?: boolean;
}

const useCreateCycleInstance = () => {
    return useMutation<CycleInstance, Error, EvaluationTypeValue>({
        mutationFn: (evaluationType: EvaluationTypeValue) =>
            cycleInstanceService.createCycleInstance(evaluationType),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [ReviewQueryKey.READY_TO_BE_LAUNCHED_REVIEWS],
            });
            queryClient.invalidateQueries({
                queryKey: [ReviewQueryKey.ONGOING_REVIEWS],
            });
        },
    });
};

const useDeleteCycleInstance = () => {
    return useMutation({
        mutationFn: cycleInstanceService.deleteCycleInstance,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [ReviewQueryKey.READY_TO_BE_LAUNCHED_REVIEWS],
            });
            queryClient.invalidateQueries({
                queryKey: [ReviewQueryKey.READY_TO_BE_LAUNCHED_REVIEWS_PAGED],
            });
            queryClient.invalidateQueries({
                queryKey: [ReviewQueryKey.ONGOING_REVIEWS],
            });
            queryClient.invalidateQueries({
                queryKey: [ReviewQueryKey.ONGOING_REVIEWS_PAGED],
            });
            queryClient.invalidateQueries({
                queryKey: [ReviewQueryKey.PAST_REVIEWS_PAGED],
            });
        },
    });
};

const useLaunchCycleInstance = () => {
    return useMutation({
        mutationFn: (cycleInstanceId: string) =>
            cycleInstanceService.launchCycleInstance(cycleInstanceId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [ReviewQueryKey.READY_TO_BE_LAUNCHED_REVIEWS],
            });
            queryClient.invalidateQueries({
                queryKey: [ReviewQueryKey.ONGOING_REVIEWS],
            });
            queryClient.invalidateQueries({
                queryKey: [ReviewQueryKey.CYCLE_INSTANCE],
            });
        },
    });
};

const useGetCycleInstanceAsync = () => {
    return useMutation({
        mutationFn: (cycleInstanceId: string) =>
            cycleInstanceService.getCycleInstance(cycleInstanceId),
    });
};

const useGetCycleInstance = (cycleInstanceId: string, options?: UseGetCycleInstanceOptions) => {
    return useQuery({
        queryKey: [
            ReviewQueryKey.CYCLE_INSTANCE,
            cycleInstanceId,
            options?.includeInactiveQuestions ? 'include-inactive' : 'active-only',
        ],
        queryFn: () => cycleInstanceService.getCycleInstance(cycleInstanceId, options),
        enabled: !!cycleInstanceId,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnMount: 'always',
    });
};

export {
    useCreateCycleInstance,
    useDeleteCycleInstance,
    useLaunchCycleInstance,
    useGetCycleInstanceAsync,
    useGetCycleInstance,
};
